// Role-based access control utilities
export const ROLES = {
    DRIVER: "Driver",
    REQUESTOR: "Requestor",
    ADMIN: "Admin",
    // Add other roles as needed
} as const;

export type RoleName = (typeof ROLES)[keyof typeof ROLES];

// Check if user has specific role
export const hasRole = (
    userRoles: string[] | undefined,
    role: RoleName
): boolean => {
    if (!userRoles || !Array.isArray(userRoles)) {
        return false;
    }
    return userRoles.some(
        (userRole) => userRole.toLowerCase() === role.toLowerCase()
    );
};

// Check if user has any of the specified roles
export const hasAnyRole = (
    userRoles: string[] | undefined,
    roles: RoleName[]
): boolean => {
    if (!userRoles || !Array.isArray(userRoles)) {
        return false;
    }
    return roles.some((role) => hasRole(userRoles, role));
};

// Check if user is a driver
export const isDriverUser = (userRoles: string[] | undefined): boolean => {
    return hasRole(userRoles, ROLES.DRIVER);
};

// Check if user is a driver (single role string version for external login)
export const isDriverUserByRole = (role: string | undefined): boolean => {
    if (!role) return false;
    return role.toLowerCase() === ROLES.DRIVER.toLowerCase();
};

// Check if user can access web portal (non-driver users)
export const canAccessWebPortal = (
    userRoles: string[] | undefined
): boolean => {
    return !isDriverUser(userRoles);
};

// Check if user can access web portal (single role string version)
export const canAccessWebPortalByRole = (role: string | undefined): boolean => {
    return !isDriverUserByRole(role);
};

// Get user's primary role (for display purposes)
export const getPrimaryRole = (userRoles: string[] | undefined): string => {
    if (!userRoles || !Array.isArray(userRoles) || userRoles.length === 0) {
        return "Unknown";
    }

    // Prioritize certain roles
    if (hasRole(userRoles, ROLES.ADMIN)) return ROLES.ADMIN;
    if (hasRole(userRoles, ROLES.DRIVER)) return ROLES.DRIVER;
    if (hasRole(userRoles, ROLES.REQUESTOR)) return ROLES.REQUESTOR;

    // Return the first role if no priority match
    return userRoles[0];
};
